import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/purchasing/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const needle = q.trim().toLowerCase();
  const visible = !needle ? items : items.filter((p) =>
    (p.name || "").toLowerCase().includes(needle) ||
    (p.category || "").toLowerCase().includes(needle) ||
    (p.unit_of_measure || "").toLowerCase().includes(needle)
  );

  const del = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    try { await api.delete(`/purchasing/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="pv-products-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Product Master"
        subtitle="Item catalogue — panels, inverters, cables, structures, and accessories."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-product-btn">
                <Plus className="w-4 h-4 mr-2" /> New Product
              </Button>
            </DialogTrigger>
            {open && <ProductDialog editing={editing} onDone={() => { setOpen(false); setEditing(null); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="relative max-w-md mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products by name, category, unit…"
            className="pl-9 h-10 rounded-none"
            data-testid="pv-products-search"
          />
        </div>
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Product", "Category", "UoM", "Status", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-product-row-${p.id}`}>
                  <td className="px-4 py-3 font-semibold">{p.name}</td>
                  <td className="px-4 py-3 text-xs">{p.category}</td>
                  <td className="px-4 py-3 text-xs font-mono">{p.unit_of_measure}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`px-2 py-0.5 uppercase tracking-widest font-bold text-[9px] ${p.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setEditing(p); setOpen(true); }} className="text-[#4B5563] hover:text-[#002FA7] mr-3" data-testid={`pv-product-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-product-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-[#9CA3AF] py-12">{needle ? `No products match "${q}".` : "No products yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProductDialog({ editing, onDone }) {
  const isEdit = !!editing;
  const [f, setF] = useState({
    name: editing?.name || "", category: editing?.category || "Other",
    unit_of_measure: editing?.unit_of_measure || "unit", active: editing?.active ?? true, notes: editing?.notes || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Product name required"); return; }
    try {
      if (isEdit) await api.patch(`/purchasing/products/${editing.id}`, f);
      else await api.post("/purchasing/products", f);
      toast.success(isEdit ? "Product updated" : "Product created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-md">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{isEdit ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div><Label className="label-eyebrow">Name *</Label><Input value={f.name} onChange={set("name")} className="rounded-none mt-2 h-10" data-testid="pv-p-name" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Category</Label><Input value={f.category} onChange={set("category")} className="rounded-none mt-2 h-10" /></div>
          <div><Label className="label-eyebrow">Unit of Measure</Label><Input value={f.unit_of_measure} onChange={set("unit_of_measure")} className="rounded-none mt-2 h-10" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4" /> Active
        </label>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-p-submit">{isEdit ? "Save" : "Create"}</Button>
      </div>
    </DialogContent>
  );
}
